import React, { useMemo, useState } from "react";
import { Card, EmptyState } from "@/hq/HQPrimitives";
import { Activity, Settings2, Repeat, FileText, CreditCard, Radar } from "lucide-react";
import { asText } from "@/hq/asText";

// event_type prefix → filter group + icon
const EVENT_GROUP = [
  { match: /^service_/,      key: "services",      Icon: Settings2 },
  { match: /^subscription_/, key: "subscriptions", Icon: Repeat },
  { match: /^installment_/,  key: "payments",      Icon: CreditCard },
  { match: /^payment_/,      key: "payments",      Icon: CreditCard },
  { match: /^invoice_/,      key: "invoices",      Icon: FileText },
  { match: /^scan_/,         key: "scans",         Icon: Radar },
  { match: /^bulk_scan/,     key: "scans",         Icon: Radar },
  { match: /^orange_score/,  key: "scans",         Icon: Radar },
];

function groupFor(activity) {
  const t = (activity.event_type || activity.entity_type || "").toLowerCase();
  for (const g of EVENT_GROUP) if (g.match.test(t)) return g;
  return { key: "other", Icon: Activity };
}

const FILTERS = [
  { key: "all",           label: "All" },
  { key: "services",      label: "Services" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "invoices",      label: "Invoices" },
  { key: "payments",      label: "Payments" },
  { key: "scans",         label: "Scans" },
];

function humanEvent(e) {
  return (e || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function actorLabel(activity) {
  const md = activity.metadata || {};
  if (md.actor) return md.actor;
  if (md.source) return md.source;
  if ((activity.event_type || "").includes("webhook")) return "Webhook";
  return "System";
}

/**
 * ClientActivityTimeline — Phase 2D.
 *
 * Reads from `core_activities` (already returned by the profile endpoint).
 * Groups events by service / subscription / invoice / payment / scan.
 * Newest-first, filterable.
 */
export default function ClientActivityTimeline({ activities }) {
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => [...(activities || [])].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  ), [activities]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filter === "all") return true;
    return groupFor(r).key === filter;
  }), [rows, filter]);

  return (
    <Card className="p-6" data-testid="hq-client-activity">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Activity</div>
          <p className="text-sm text-[#4A4A4A] mt-1">System events for this client — service changes, subscriptions, invoices, payments and scans.</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`hq-activity-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${filter === f.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] hover:bg-[#F6F5F2]"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6"><EmptyState title="No activity yet" testId="hq-client-activity-empty"/></div>
      ) : (
        <ol className="mt-5 relative border-l-2 border-[#EAEAEA] ml-3">
          {filtered.map((a) => {
            const g = groupFor(a);
            return (
              <li key={a.id} className="mb-4 ml-6 relative" data-testid={`hq-activity-row-${a.id}`}>
                <span className="absolute -left-[34px] top-0 w-7 h-7 rounded-full bg-white border-2 border-[#FF6A00] flex items-center justify-center text-[#FF6A00]">
                  <g.Icon size={12}/>
                </span>
                <div className="text-[10px] tracking-[0.18em] uppercase font-black text-[#8A4A00]">
                  {humanEvent(a.event_type) || "Event"}
                </div>
                <div className="mt-0.5 text-[14px] font-bold text-[#0A0A0A]">{asText(a.description) || humanEvent(a.event_type)}</div>
                <div className="mt-0.5 text-[11px] text-[#8A8A8A]">
                  {a.created_at ? new Date(a.created_at).toLocaleString() : "—"} · <span className="uppercase tracking-[0.14em] font-black text-[#4A4A4A]">{actorLabel(a)}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
